<script setup>
// NOTICE: KAPTION-INTEGRATION
// Enhanced WhatsApp Preview component using wapreview.kaptionai.com/show
// Supports full postMessage API with edit/bind modes
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import themeEventEmitter from 'shared/helpers/themeEventEmitter';

// NOTICE: KAPTION-INTEGRATION - Enhanced props for full wapreview.kaptionai.com/show support
const props = defineProps({
  // The full template object from WhatsApp
  template: {
    type: Object,
    default: null,
  },
  // Variable slots with bindings and available options
  // Format: [{ slot: '0', binding: '{{ contact.name }}', availableBindings: [...] }]
  variables: {
    type: Array,
    default: () => [],
  },
  // Resolved values for bindings
  // Format: { '{{ contact.name }}': 'John Smith', ... }
  resolvedValues: {
    type: Object,
    default: () => ({}),
  },
  // View mode: render, edit, bind, renderBind
  mode: {
    type: String,
    default: 'render',
    validator: v => ['render', 'edit', 'bind', 'renderBind'].includes(v),
  },
  // Loading state
  loading: {
    type: Boolean,
    default: false,
  },
  // Show bare mode (message only) or full phone frame
  bare: {
    type: Boolean,
    default: false,
  },
  // Company name to show in phone header
  companyName: {
    type: String,
    default: 'Business',
  },
  // NOTICE: KAPTION-INTEGRATION - Enable text truncation for MARKETING templates
  // When true, shows "Read more" for messages with more than 5 lines
  truncate: {
    type: Boolean,
    default: false,
  },
  // NOTICE: KAPTION-INTEGRATION - Card title (tap_target_configuration).
  // When set and the template has a URL button, the preview shows the
  // tappable-card link preview: this title + the tracked-link domain.
  tapTargetTitle: {
    type: String,
    default: '',
  },
  // Tracked-link domain shown as the card subtitle. Defaults to kapt.io in
  // the preview worker; override for accounts on their own shortener domain.
  linkDomain: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['bindingsUpdate', 'stateUpdate']);

const PREVIEW_URL = 'https://wapreview.kaptionai.com/show';
const PREVIEW_ORIGIN = new URL(PREVIEW_URL).origin;
const INIT_RETRY_DELAYS = [50, 300, 900, 2000];
const STATE_PROBE_DELAY = 120;
const RETRY_CYCLE_DELAY = 3000;

const iframeRef = ref(null);
const iframeLoaded = ref(false);
const previewReady = ref(false);
const isInitialized = ref(false);
const initializationTimers = new Set();

// Detect current theme
const isDarkMode = ref(document.body.classList.contains('dark'));

// NOTICE: KAPTION-INTEGRATION - Build template config for the preview
// Supports TEXT, IMAGE, and DOCUMENT header formats, plus template category
const templateConfig = computed(() => {
  if (!props.template) return null;

  const config = {
    // Include template category for proper truncation behavior (MARKETING/UTILITY/AUTHENTICATION)
    category: props.template.category || 'UTILITY',
  };

  props.template.components?.forEach(component => {
    if (component.type === 'HEADER') {
      const format = component.format || 'TEXT';
      config.headerFormat = format;

      if (format === 'TEXT') {
        config.header = component.text || '';
      } else if (format === 'IMAGE') {
        // For IMAGE headers, use example URL if available
        const exampleUrl =
          component.example?.header_handle?.[0] ||
          component.example?.header_url?.[0] ||
          component.example?.[0];
        if (exampleUrl) {
          config.headerMediaUrl = exampleUrl;
        }
      } else if (format === 'DOCUMENT') {
        // For DOCUMENT headers, use example URL and filename
        const exampleUrl =
          component.example?.header_handle?.[0] ||
          component.example?.header_url?.[0] ||
          component.example?.[0];
        if (exampleUrl) {
          config.headerMediaUrl = exampleUrl;
        }
        // Try to extract filename from template or use a default
        config.headerDocumentFilename =
          component.document_filename || 'Document.pdf';
      } else if (format === 'VIDEO') {
        // For VIDEO headers, use example URL
        const exampleUrl =
          component.example?.header_handle?.[0] ||
          component.example?.header_url?.[0] ||
          component.example?.[0];
        if (exampleUrl) {
          config.headerMediaUrl = exampleUrl;
        }
      }
    } else if (component.type === 'BODY') {
      config.body = component.text || '';
    } else if (component.type === 'FOOTER') {
      config.footer = component.text || '';
    } else if (component.type === 'BUTTONS') {
      config.buttons = component.buttons?.map(btn => ({
        type: btn.type || 'QUICK_REPLY',
        text: btn.text || '',
        ...(btn.url && { url: btn.url }),
        ...(btn.phone_number && { phone_number: btn.phone_number }),
      }));
    }
  });

  // NOTICE: KAPTION-INTEGRATION - Link-preview card (tap_target): the worker
  // renders the card only when a URL button exists and no media header.
  if (props.tapTargetTitle?.trim()) {
    config.tapTargetTitle = props.tapTargetTitle.trim();
  }
  if (props.linkDomain?.trim()) {
    config.linkDomain = props.linkDomain.trim();
  }

  return config;
});

const clearInitializationTimers = () => {
  initializationTimers.forEach(timer => clearTimeout(timer));
  initializationTimers.clear();
};

const stateMatchesTemplate = stateTemplate => {
  const expected = templateConfig.value;
  if (!expected || !stateTemplate) return false;

  const scalarKeys = [
    'category',
    'headerFormat',
    'header',
    'headerMediaUrl',
    'headerDocumentFilename',
    'body',
    'footer',
    'tapTargetTitle',
    'linkDomain',
  ];
  const scalarsMatch = scalarKeys.every(
    key => (stateTemplate[key] ?? null) === (expected[key] ?? null)
  );

  return (
    scalarsMatch &&
    JSON.stringify(stateTemplate.buttons || []) ===
      JSON.stringify(expected.buttons || [])
  );
};

// Listen for messages from iframe
const handleIframeMessage = event => {
  // Each page can host many preview iframes. Accept only messages from this
  // component's own frame so one preview cannot acknowledge every card.
  if (event.origin !== PREVIEW_ORIGIN) return;
  if (event.source !== iframeRef.value?.contentWindow) return;

  const { data } = event;

  if (data?.type === 'BINDINGS_UPDATE') {
    // User changed bindings in edit mode
    emit('bindingsUpdate', data.bindings);
  } else if (data?.type === 'STATE_UPDATE') {
    if (stateMatchesTemplate(data.state?.template)) {
      previewReady.value = true;
      clearInitializationTimers();
    }
    // Response to GET_STATE request
    emit('stateUpdate', data.state);
  }
};

// NOTICE: KAPTION-INTEGRATION - Send INIT message to iframe with full config
const initIframe = () => {
  if (!iframeRef.value || !iframeLoaded.value) return;
  // NOTICE: KAPTION-INTEGRATION — never hand the iframe a null/unresolved template;
  // wapreview reads template.headerFormat and throws on null. INIT only once a real
  // template exists; the watch below INITs when one arrives.
  if (!templateConfig.value) return;

  const message = {
    type: 'INIT',
    template: templateConfig.value,
    variables: props.variables,
    resolvedValues: props.resolvedValues,
    mode: props.mode,
    theme: isDarkMode.value ? 'dark' : 'light',
    bare: props.bare,
    // Enable truncation for MARKETING templates (shows "Read more" for 5+ lines)
    truncate: props.truncate,
    // Hide iframe's internal loading overlay once we send data
    loading: false,
  };

  iframeRef.value.contentWindow?.postMessage(message, '*');
  isInitialized.value = true;
};

// Send SET_LOADING message to control iframe's internal loading overlay
const setLoading = loading => {
  if (!iframeRef.value || !iframeLoaded.value) return;

  iframeRef.value.contentWindow?.postMessage(
    { type: 'SET_LOADING', loading },
    '*'
  );
};

// Send UPDATE_TEMPLATE message
const updateTemplate = () => {
  if (!iframeRef.value || !iframeLoaded.value || !isInitialized.value) return;
  // NOTICE: KAPTION-INTEGRATION — skip null templates (see initIframe). Posting
  // template:null to wapreview throws "Cannot read properties of null (headerFormat)"
  // — the bug when the warm iframe is retargeted to an unresolved campaign.
  if (!templateConfig.value) return;

  const message = {
    type: 'UPDATE_TEMPLATE',
    template: templateConfig.value,
    variables: props.variables,
    resolvedValues: props.resolvedValues,
  };

  iframeRef.value.contentWindow?.postMessage(message, '*');
};

// Send SET_MODE message
const setMode = mode => {
  if (!iframeRef.value || !iframeLoaded.value) return;

  iframeRef.value.contentWindow?.postMessage({ type: 'SET_MODE', mode }, '*');
};

// Send SET_THEME message
const setTheme = theme => {
  if (!iframeRef.value || !iframeLoaded.value) return;

  iframeRef.value.contentWindow?.postMessage({ type: 'SET_THEME', theme }, '*');
};

// Send SET_BARE message
const setBare = bare => {
  if (!iframeRef.value || !iframeLoaded.value) return;

  iframeRef.value.contentWindow?.postMessage({ type: 'SET_BARE', bare }, '*');
};

// NOTICE: KAPTION-INTEGRATION - Send SET_TRUNCATE message for dynamic truncation toggle
const setTruncate = truncate => {
  if (!iframeRef.value || !iframeLoaded.value) return;

  iframeRef.value.contentWindow?.postMessage(
    { type: 'SET_TRUNCATE', truncate },
    '*'
  );
};

// Request current state from iframe
const getState = () => {
  if (!iframeRef.value || !iframeLoaded.value) return;

  iframeRef.value.contentWindow?.postMessage({ type: 'GET_STATE' }, '*');
};

// wapreview currently has no READY event. Probe its existing GET_STATE API and
// retry INIT until the iframe confirms the expected template. This prevents a
// busy page with many frames from losing the only initialization message.
const scheduleInitialization = ({ updateFirst = false } = {}) => {
  clearInitializationTimers();
  previewReady.value = false;
  if (!iframeLoaded.value || !templateConfig.value) return;

  INIT_RETRY_DELAYS.forEach((delay, index) => {
    const initTimer = setTimeout(() => {
      initializationTimers.delete(initTimer);
      if (previewReady.value) return;

      if (index === 0 && updateFirst && isInitialized.value) {
        updateTemplate();
      } else {
        initIframe();
      }

      const probeTimer = setTimeout(() => {
        initializationTimers.delete(probeTimer);
        if (!previewReady.value) getState();
      }, STATE_PROBE_DELAY);
      initializationTimers.add(probeTimer);
    }, delay);
    initializationTimers.add(initTimer);
  });

  const restartTimer = setTimeout(
    () => {
      initializationTimers.delete(restartTimer);
      if (!previewReady.value) scheduleInitialization();
    },
    INIT_RETRY_DELAYS.at(-1) + STATE_PROBE_DELAY + RETRY_CYCLE_DELAY
  );
  initializationTimers.add(restartTimer);
};

// Handle iframe load
const handleIframeLoad = () => {
  iframeLoaded.value = true;
  scheduleInitialization();
};

// Listen for theme changes
const handleThemeChange = event => {
  isDarkMode.value = event.theme === 'dark';
  setTheme(isDarkMode.value ? 'dark' : 'light');
};

// Watch for template/variables changes
watch(
  [templateConfig, () => props.variables, () => props.resolvedValues],
  () => {
    // NOTICE: KAPTION-INTEGRATION — one warm iframe is retargeted across rows: INIT
    // on the first real template, UPDATE thereafter. Both skip null (guarded above),
    // so retargeting to an unresolved campaign never crashes the iframe.
    scheduleInitialization({ updateFirst: isInitialized.value });
  },
  { deep: true }
);

// Watch for mode changes
watch(
  () => props.mode,
  newMode => {
    setMode(newMode);
  }
);

// Watch for bare changes
watch(
  () => props.bare,
  newBare => {
    setBare(newBare);
  }
);

// NOTICE: KAPTION-INTEGRATION - Watch for truncate changes
watch(
  () => props.truncate,
  newTruncate => {
    setTruncate(newTruncate);
  }
);

let unsubscribeTheme = null;

onMounted(() => {
  unsubscribeTheme = themeEventEmitter.onThemeChange(handleThemeChange);
  window.addEventListener('message', handleIframeMessage);
});

onUnmounted(() => {
  clearInitializationTimers();
  if (unsubscribeTheme) {
    unsubscribeTheme();
  }
  window.removeEventListener('message', handleIframeMessage);
});

// Build the iframe URL once at component creation
// All updates after initial load happen via postMessage (no iframe reload)
const buildInitialSrc = () => {
  const params = new URLSearchParams({
    mode: props.mode,
    bare: props.bare ? 'true' : 'false',
    dark: isDarkMode.value ? 'true' : 'false',
  });
  if (!props.bare && props.companyName) {
    params.set('company_name', props.companyName);
  }
  return `${PREVIEW_URL}?${params.toString()}`;
};

// Static iframe URL - set once at component creation, never changes
// This prevents iframe reload when mode/bare/theme changes
const iframeSrc = buildInitialSrc();

// NOTICE: KAPTION-INTEGRATION - Expose methods for parent component
defineExpose({
  getState,
  setMode,
  setTheme,
  setBare,
  setLoading,
  setTruncate,
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div
    class="kaption-whatsapp-preview-enhanced relative overflow-hidden rounded-xl"
    :class="[
      bare
        ? isDarkMode
          ? 'bg-[#0b141a]'
          : 'bg-[#efeae2]'
        : isDarkMode
          ? 'bg-[#0b141a]'
          : 'bg-[#e5ddd5]',
    ]"
  >
    <!-- Loading overlay -->
    <div
      v-if="loading || !previewReady"
      class="absolute inset-0 flex items-center justify-center z-10"
      :class="
        bare
          ? isDarkMode
            ? 'bg-[#0b141a]'
            : 'bg-[#efeae2]'
          : 'bg-n-alpha-black1'
      "
    >
      <div class="flex flex-col items-center gap-2">
        <div
          class="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
          :class="isDarkMode ? 'border-[#00a884]' : 'border-[#25d366]'"
        />
        <span
          class="text-xs"
          :class="isDarkMode ? 'text-[#8696a0]' : 'text-[#667781]'"
        >
          Loading preview...
        </span>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-if="!template && !loading"
      class="flex items-center justify-center h-full min-h-[200px]"
    >
      <span
        class="text-sm"
        :class="isDarkMode ? 'text-[#8696a0]' : 'text-[#667781]'"
      >
        Select a template to preview
      </span>
    </div>

    <!-- iframe preview -->
    <iframe
      v-show="template && !loading"
      ref="iframeRef"
      :src="iframeSrc"
      loading="lazy"
      class="w-full h-full min-h-[200px] border-0 rounded-xl"
      title="WhatsApp Template Preview"
      sandbox="allow-scripts allow-same-origin"
      @load="handleIframeLoad"
    />
  </div>
  <!-- eslint-enable vue/no-bare-strings-in-template -->
</template>

<style scoped>
.kaption-whatsapp-preview-enhanced {
  /* Ensure iframe fills container */
}
</style>
